import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { useSearchParams } from "react-router-dom";
import { useQueryParam } from "@/hooks/use-query-param";
import { useState, useEffect } from "react";

interface DateRange {
  from: string;
  to: string;
}

export function FilterBar() {
  const [today] = useState(moment.tz(CommonDestinationTimezone).startOf("day"));
  const [searchParams, setSearchParams] = useSearchParams();
  const [fromParam, setFromParam] = useQueryParam("from", today.clone().startOf("day").format("YYYY-MM-DD"));
  const [toParam, setToParam] = useQueryParam("to", today.clone().endOf("day").format("YYYY-MM-DD"));
  const [pageParam, setPageParam] = useQueryParam("page", "1");

  const presets = [
    {
      label: "Today",
      getValue: () => ({
        from: today.clone().startOf("day").format("YYYY-MM-DD"),
        to: today.clone().endOf("day").format("YYYY-MM-DD"),
      }),
    },
    {
      label: "Yesterday",
      getValue: () => ({
        from: today.clone().subtract(1, "days").startOf("day").format("YYYY-MM-DD"),
        to: today.clone().subtract(1, "days").endOf("day").format("YYYY-MM-DD"),
      }),
    },
    {
      label: "Current Week",
      getValue: () => ({
        from: today.clone().startOf("week").format("YYYY-MM-DD"),
        to: today.clone().endOf("week").format("YYYY-MM-DD"),
      }),
    },
    {
      label: "Last Week",
      getValue: () => ({
        from: today.clone().startOf("week").subtract(1, "week").format("YYYY-MM-DD"),
        to: today.clone().startOf("week").subtract(1, "week").endOf("week").format("YYYY-MM-DD"),
      }),
    },
    {
      label: "Current Month",
      getValue: () => ({
        from: today.clone().startOf("month").format("YYYY-MM-DD"),
        to: today.clone().endOf("month").format("YYYY-MM-DD"),
      }),
    },
  ];

  // Handle date range change and sync with query params
  const handleDateRangeChange = (newRange: DateRange) => {
    // Use setSearchParams to update both params at once
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("from", newRange.from);
      params.set("to", newRange.to);
      params.set("page", "1")
      return params;
    }, { replace: true });
  };

  const dateRange = { from: fromParam, to: toParam };

  // Check if a preset is currently selected
  const isPresetSelected = (preset: typeof presets[0]) => {
    const presetValue = preset.getValue();
    return dateRange.from === presetValue.from && dateRange.to === presetValue.to;
  };

  return (
    <Card className="p-4 mb-4">
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {presets.map((preset) => (
            <Button
              key={preset.label}
              variant={isPresetSelected(preset) ? "default" : "outline"}
              size="sm"
              className={cn(isPresetSelected(preset) ? "inline-flex items-center justify-center text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-black border hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3 border-yellow-400 bg-yellow-200" : "")}
              onClick={() => handleDateRangeChange(preset.getValue())}
            >
              {preset.label}
            </Button>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Label htmlFor="date-from">From Date</Label>
            <Input
              id="date-from"
              type="date"
              value={dateRange.from}
              onChange={(e) =>
                handleDateRangeChange({ ...dateRange, from: e.target.value })
              }
              max={dateRange.to}
            />
          </div>

          <div className="flex-1">
            <Label htmlFor="date-to">To Date</Label>
            <Input
              id="date-to"
              type="date"
              value={dateRange.to}
              onChange={(e) =>
                handleDateRangeChange({ ...dateRange, to: e.target.value })
              }
              min={dateRange.from}
              max={today.format("YYYY-MM-DD")}
            />
          </div>
        </div>
      </div>
    </Card>
  );
}
